import { useEffect, useRef } from "react"
import {
  Turnstile as ReactTurnstile,
  TurnstileInstance,
} from "@marsidev/react-turnstile"

import { Emitter } from "@/lib/emitter"
import { env } from "@/lib/env"
import { turnstileErrorAction } from "@/lib/turnstile"
import { ClientRender } from "@/components/client-render"

export interface InvisibleTurnstileProps {
  id: string
  refreshEmitter?: Emitter<void>
  onVerify: (token: string) => void
  onError: (errorCode: string) => void
  onUnsupported?: () => void
  onScriptLoadError?: () => void
}

// There are some codes you can use below to get specific errors from cloudflare turnstile
// https://developers.cloudflare.com/turnstile/troubleshooting/testing/

function InternalTurnstile({
  id,
  refreshEmitter,
  onVerify,
  onError,
  onUnsupported,
  onScriptLoadError,
}: InvisibleTurnstileProps) {
  const turnstileRef = useRef<TurnstileInstance>(null)

  useEffect(() => {
    const listener = () => {
      turnstileRef.current?.reset()
    }

    refreshEmitter?.addListener(listener)

    return () => {
      refreshEmitter?.removeListener(listener)
    }
  }, [refreshEmitter, turnstileRef])

  const siteKey = env("NEXT_PUBLIC_CF_INVISIBLE_TURNSTILE_SITE_KEY") ?? ""

  function handleError(errorCode: string) {
    onError(errorCode)

    if (turnstileErrorAction(errorCode) === "reset") {
      turnstileRef.current?.reset()
    }
  }

  return (
    <ReactTurnstile
      id={id}
      ref={turnstileRef}
      options={{
        size: "invisible",
        theme: "light",
      }}
      siteKey={siteKey}
      key={siteKey}
      onSuccess={onVerify}
      onError={handleError}
      onUnsupported={onUnsupported}
      scriptOptions={{ onError: onScriptLoadError }}
    />
  )
}

// must be client rendered as it loads a js script
export function InvisibleTurnstile({
  id,
  refreshEmitter,
  onVerify,
  onError,
  onUnsupported,
  onScriptLoadError,
}: InvisibleTurnstileProps) {
  const enabled = !!env("NEXT_PUBLIC_CF_INVISIBLE_TURNSTILE_SITE_KEY")
  const init = useRef(false)

  useEffect(() => {
    if (!init.current) {
      init.current = true

      if (!enabled) {
        onError("[turnstile_disabled]: Invisible Turnstile is not enabled")
      }
    }
  }, [init, enabled, onError])

  if (!enabled) {
    return null
  }

  return (
    <ClientRender>
      <InternalTurnstile
        id={id}
        refreshEmitter={refreshEmitter}
        onVerify={onVerify}
        onError={onError}
        onUnsupported={onUnsupported}
        onScriptLoadError={onScriptLoadError}
      />
    </ClientRender>
  )
}
